import {
  type ApiClient,
  type ApiClientOptions,
  ApiRequestError,
  createApiClient,
} from '@croissants/contracts';
import { focusManager, QueryClient } from '@tanstack/react-query';

/**
 * The browser transport every portal shares (F6): where its requests go, the
 * client that sends them, and the query client its reads run on.
 *
 * Four portals each resolved their API origin and built their QueryClient on
 * their own - four `resolveApiBaseUrl`s (one with a hardcoded local host as a
 * fallback) and four `makeQueryClient`s, held equal only by a source scan that
 * read each copy. The parts that are the same are here once; what genuinely
 * differs - a guest's proxy prefix, a guest's retry rule, the tablet's cache
 * defaults, each portal's credential headers - is passed in.
 *
 * Reading `import.meta.env` stays in each app: a `VITE_` variable is
 * substituted from the app's OWN build, and `check-vite-vars` matches each
 * app's declarations against what its own `src` reads.
 */

// ---------------------------------------------------------------------------
// The API client
// ---------------------------------------------------------------------------

export interface PortalClientOptions {
  /** `resolveApiBaseUrl`'s answer. */
  readonly baseUrl: string;
  /** The portal's own credential and locale headers, read at call time. */
  readonly headers?: ApiClientOptions['headers'];
  /** A portal that observes its own traffic (the counter's reachability). */
  readonly fetchImpl?: ApiClientOptions['fetchImpl'];
  /**
   * Whether the founder's pilot cookie (docs/pilot-access.html) rides on every
   * call. The staff portals are on `app.`/`baker.`/`internal.` and the API on
   * `api.`, so a cross-origin fetch sends no cookie unless told to and every
   * pilot request would arrive anonymous; the device, Clerk and dev paths carry
   * their credentials in headers and are unaffected. Safe because the worker's
   * CORS layer echoes only its own allowlisted origins, never `*`.
   *
   * The guest says no: its session is the `x-guest-session` header and no
   * pilot session opens `/g`, so a credentialed request would buy it nothing
   * and change the CORS exchange every guest call makes.
   */
  readonly pilotCookie: boolean;
}

/**
 * The pilot-cookie policy (`PortalClientOptions.pilotCookie`), stated ONCE.
 *
 * Owns: the fetch `credentials` a portal's requests carry. Both of a portal's
 * doors spread it - `createPortalClient` below and `createApiProvider`'s raw
 * `fetchRaw` - so a JSON call and a file download cannot disagree about whether
 * the cookie rides. Refuses: any other credentials mode. A portal that declines
 * the cookie names no `credentials` at all, so the browser's default applies and
 * the CORS exchange is the one it always was.
 */
export function pilotCookieCredentials(pilotCookie: boolean): {
  readonly credentials?: 'include';
} {
  return pilotCookie ? { credentials: 'include' } : {};
}

/**
 * The one API client a portal builds. Responses are always validated against
 * the endpoint's contract (founder, D9 of 2026-10-06), so there is no option to
 * turn that off: a portal that skipped it read a `.default()` field the server
 * left out as `undefined`, and every property partner's Analytics tab was a
 * white page.
 */
export function createPortalClient(options: PortalClientOptions): ApiClient {
  return createApiClient({
    baseUrl: options.baseUrl,
    ...(options.headers === undefined ? {} : { headers: options.headers }),
    ...(options.fetchImpl === undefined ? {} : { fetchImpl: options.fetchImpl }),
    ...pilotCookieCredentials(options.pilotCookie),
  });
}

// ---------------------------------------------------------------------------
// Where requests go
// ---------------------------------------------------------------------------

export interface ApiBaseUrlInput {
  /** `VITE_API_BASE_URL` as the build read it; empty when unset. */
  readonly configured: string | undefined;
  /** `import.meta.env.DEV`: the vite dev server, never a built bundle. */
  readonly dev: boolean;
  /** The page's own origin, when there is one. */
  readonly origin?: string | undefined;
  /**
   * What the dev server proxies the API under: '' for the portals whose vite
   * config proxies the API's own mount prefixes (`/b`, `/c`, `/internal`), and
   * the guest's `/api`, which its vite config strips before forwarding.
   */
  readonly devProxyPrefix?: string;
}

/**
 * The prefix every request is built from. No host is ever written here (PRD
 * OPS-018): a built bundle uses the configured value verbatim, trailing slashes
 * trimmed, and an unset one is '' - same origin - rather than a guessed local
 * worker. In the dev server a value on ANOTHER origin, or none at all, becomes
 * the same-origin proxy prefix, because the worker answers no CORS preflight
 * and the vite proxy is what reaches it; a relative value, or one on this very
 * origin, passes through untouched.
 */
export function resolveApiBaseUrl(input: ApiBaseUrlInput): string {
  const value = (input.configured ?? '').trim().replace(/\/+$/, '');
  if (!input.dev) return value;
  const proxy = input.devProxyPrefix ?? '';
  if (value === '') return proxy;
  if (!/^https?:\/\//i.test(value)) return value;
  try {
    if (input.origin !== undefined && new URL(value).origin === input.origin) return value;
  } catch {
    return value;
  }
  return proxy;
}

// ---------------------------------------------------------------------------
// The query client
// ---------------------------------------------------------------------------

/**
 * The retry rule three portals share: a 4xx is the server's considered answer
 * and asking again changes nothing, anything else is tried twice more.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500) {
    return false;
  }
  return failureCount < 2;
}

export interface QueryClientOptions {
  /** A portal's own retry rule; the shared one above when omitted. */
  readonly shouldRetry?: (failureCount: number, error: unknown) => boolean;
  /** How long a read is fresh. 30 s on every portal today. */
  readonly staleTime?: number;
}

/**
 * The one QueryClient a portal builds, carrying the three parking invariants
 * `scripts/check-no-eternal-spinner.mjs` holds this file to and
 * `query-parking.test.ts` proves sufficient:
 *
 * - `focusManager.setFocused(true)`: the retryer resumes a retry only while the
 *   tab is focused, so a hidden tab - a locked phone - parks every retry
 *   without it, whatever the network mode says;
 * - `networkMode: 'always'`, on reads AND writes: the default parks a fetch on
 *   a connection the browser calls offline and this platform can still reach,
 *   and a parked mutation leaves its button spinning for ever;
 * - a retry budget that ends.
 *
 * Reads are not refetched on window focus - every portal is a short session on
 * a possibly poor connection, refetched on reconnect - and a write is never
 * retried automatically: every write that moves money carries an idempotency
 * key, but a silent second attempt would still hide a failure the reader has
 * to see (PAY-006).
 */
export function makeQueryClient(options: QueryClientOptions = {}): QueryClient {
  focusManager.setFocused(true);
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: options.shouldRetry ?? shouldRetryQuery,
        networkMode: 'always',
        staleTime: options.staleTime ?? 30_000,
      },
      mutations: {
        retry: 0,
        networkMode: 'always',
      },
    },
  });
}
